import { useState } from 'react';

const KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#']
] as const;

interface CallKeypadProps {
  onDigit: (digit: string) => void;
}

/**
 * Keypad for working through a switchboard menu on a live call.
 *
 * Each press sends a DTMF tone straight down the call; there is nothing to submit and no way to
 * correct a digit once sent, so the entered sequence is shown purely as a record of what the far
 * end has already heard.
 */
export default function CallKeypad({ onDigit }: CallKeypadProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [entered, setEntered] = useState('');

  const press = (digit: string) => {
    onDigit(digit);
    setEntered((previous) => (previous + digit).slice(-20));
  };

  const close = () => {
    setIsOpen(false);
    setEntered('');
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
        onClick={() => setIsOpen(true)}
        data-testid="call-keypad-open"
      >
        Keypad
      </button>
    );
  }

  return (
    <div
      className="mt-1 w-full rounded border border-gray-300 bg-white p-2 shadow"
      data-testid="call-keypad-panel"
    >
      <p
        className="mb-2 min-h-[1.25rem] truncate text-center font-mono text-sm text-gray-700"
        aria-live="polite"
        data-testid="call-keypad-entered"
      >
        {entered}
      </p>

      <div className="grid grid-cols-3 gap-1">
        {KEYS.flat().map((digit) => (
          <button
            key={digit}
            type="button"
            className="rounded border border-gray-200 py-2 text-base font-medium text-gray-800 hover:bg-gray-100"
            onClick={() => press(digit)}
            aria-label={`Press ${digit}`}
            data-testid={`call-keypad-${digit}`}
          >
            {digit}
          </button>
        ))}
      </div>

      <button
        type="button"
        className="mt-2 w-full rounded px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
        onClick={close}
        data-testid="call-keypad-close"
      >
        Close
      </button>
    </div>
  );
}
